import type { IndexKind as IndexKindType } from '#layers/data-model/constants/schema/schema';
import type { GlobalCollectionId, GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId } from '#layers/data-model/types/schema/ids';
import type { MonospacePrimitiveField } from '#layers/data-model/types/schema/primitive-field';
import type {
	ResolvedMonospaceCollection,
	ResolvedMonospaceIndex,
	ResolvedMonospacePrimitiveField,
	ResolvedMonospaceSingleRelationField,
} from '#layers/data-model/types/schema/resolved-schema';
import type { MonospaceSingleRelationField } from '#layers/data-model/types/schema/workspace-schema';
import { ensureArray, isNullishOrEmpty, randomUuid } from '#layers/base/public';
import { IndexKind } from '#layers/data-model/constants/schema/schema';
import { isPrimitiveTypeUnsupported } from '#layers/data-model/utils/schema/primitive-type';

/**
 * Retrieves the primary key fields for a given collection.
 *
 * @param {ResolvedMonospaceCollection} collection - The resolved Monospace collection from which to extract primary key fields.
 * @return {ResolvedMonospacePrimitiveField[]} An array of primitive fields representing the primary keys of the collection.
 */
export function getCollectionPrimaryKeyFields(collection: ResolvedMonospaceCollection): ResolvedMonospacePrimitiveField[] {
	return (collection.indexes?.filter((index) => index.kind === IndexKind.Primary)
		.flatMap((index) => index.fields?.map((indexField) => indexField.field))
		.filter(Boolean) ?? []) as ResolvedMonospacePrimitiveField[];
}

/**
 * The api names of the primary key fields: what an rstore key for the collection is built from.
 */
export function getCollectionPrimaryKeyFieldNames(collection: ResolvedMonospaceCollection): string[] {
	return getCollectionPrimaryKeyFields(collection).map((field) => field.apiName);
}

/**
 * Retrieves the fields that identify a row of the given collection.
 *
 * This is the primary key where one exists, and otherwise the narrowest unique index. A junction
 * table is routinely declared with only a unique constraint over its two foreign keys, and such a
 * collection still has a stable identity even though it has no primary key: callers that need to
 * key, cache or re-fetch a row want this rather than {@link getCollectionPrimaryKeyFields}, which
 * reports nothing for them.
 */
export function getCollectionIdentityFields(collection: ResolvedMonospaceCollection): ResolvedMonospacePrimitiveField[] {
	const primaryKeyFields = getCollectionPrimaryKeyFields(collection);

	if (primaryKeyFields.length > 0) return primaryKeyFields;

	const narrowestUniqueIndex = (collection.indexes ?? [])
		.filter((index) => index.kind === IndexKind.Unique && index.fields?.length)
		.toSorted((a, b) => a.fields.length - b.fields.length)[0];

	return (narrowestUniqueIndex?.fields?.map((indexField) => indexField.field).filter(Boolean) ?? []) as ResolvedMonospacePrimitiveField[];
}

/**
 * Returns true if the provided field is a primary key field.
 */
export function isPrimaryKeyField(field: ResolvedMonospacePrimitiveField) {
	return getCollectionPrimaryKeyFields(field.collection!).map((field) => field.id).includes(field.id);
}

/**
 * Checks if the provided collection has a composite primary key.
 *
 * A composite primary key is determined by having more than one field
 * in the primary key of the collection.
 *
 * @param {ResolvedMonospaceCollection} collection - The collection to check for a composite primary key.
 * @return {boolean} Returns true if the collection has a composite primary key, otherwise false.
 */
export function hasCompositePrimaryKey(collection: ResolvedMonospaceCollection): boolean {
	return getCollectionPrimaryKeyFields(collection).length > 1;
}

export interface GetCollectionFieldsOptions<P extends boolean = true, R extends boolean = true> {
	/**
	 * Include primitive fields in the return.
	 *
	 * @default true
	 */
	includePrimitiveFields?: P;
	/**
	 * Include relation fields in the return.
	 *
	 * @default true
	 */
	includeRelationFields?: R;
	/**
	 * Include the constrained primitive fields when listing the collection fields.
	 *
	 * @default true
	 */
	includeConstrainedFields?: boolean;
	/**
	 * Include primitive fields with unsupported types.
	 *
	 * @default true
	 */
	includeUnsupportedFields?: boolean;
}

type GetCollectionFieldsReturn<P extends boolean, R extends boolean> = P extends true
	? R extends true
		? (ResolvedMonospaceSingleRelationField | ResolvedMonospacePrimitiveField)[]
		: ResolvedMonospacePrimitiveField[]
	: R extends true ? ResolvedMonospaceSingleRelationField[] : never;

/**
 * Retrieves the fields of a given collection, including primitive and relation fields.
 */
export function getCollectionFields<P extends boolean = true, R extends boolean = true>(collection: ResolvedMonospaceCollection, options?: GetCollectionFieldsOptions<P, R>): GetCollectionFieldsReturn<P, R> {
	const { includePrimitiveFields = true, includeRelationFields = true, includeConstrainedFields = true, includeUnsupportedFields = true } = options ?? {};

	const primitiveFields = collection.primitiveFields
		.filter((f) => includeConstrainedFields || isNullishOrEmpty(f.constrainedBy))
		.filter((f) => includeUnsupportedFields || !isPrimitiveTypeUnsupported(f.type));

	return [
		...(includePrimitiveFields ? primitiveFields : []),
		...(includeRelationFields ? collection.relationFields : []),
	] as unknown as GetCollectionFieldsReturn<P, R>;
}

type GetPermittedCollectionFieldsOptions<P extends boolean = true, R extends boolean = true> = GetCollectionFieldsOptions<P, R> & {
	canRead: (fieldId: GlobalFieldId) => boolean;
};

export function getPermittedCollectionFields<P extends boolean = true, R extends boolean = true>(collection: ResolvedMonospaceCollection, options: GetPermittedCollectionFieldsOptions<P, R>): GetCollectionFieldsReturn<P, R> {
	const fields = getCollectionFields(collection, options);

	return fields.filter(({ id }) => options.canRead(id)) as GetCollectionFieldsReturn<P, R>;
}

/**
 * Retrieves a list of indexes associated with a given primitive field.
 *
 * @param {ResolvedMonospacePrimitiveField} field - The primitive field for which to retrieve the indexes.
 * @param {object} [options] - Optional configuration for filtering the indexes.
 * @param {IndexKindType | IndexKindType[]} [options.kind] - Specific kinds of indexes to filter by.
 * @return {ResolvedMonospaceIndex[]} The list of indexes that match the given criteria.
 */
export function getIndexesForPrimitiveField(field: ResolvedMonospacePrimitiveField, options?: { kind?: IndexKindType | IndexKindType[] }): ResolvedMonospaceIndex[] {
	const kinds = ensureArray(options?.kind ?? []) as IndexKindType[];
	return field?.indexedBy?.map((indexField) => indexField.index!).filter((index) => index && (!options?.kind || kinds.includes(index.kind))) ?? [];
}

/**
 * Determines whether the given primitive field is part of a primary key or a composite primary key.
 *
 * @param field The resolved primitive field to evaluate.
 * @return An object containing:
 *         - isPrimaryKey: A boolean indicating if the field is a standalone primary key.
 *         - isCompositePrimaryKey: A boolean indicating if the field is part of a composite primary key.
 */
export function getPrimitiveFieldPrimaryKeyInfo(field: ResolvedMonospacePrimitiveField): {
	isPrimaryKey: boolean;
	isCompositePrimaryKey: boolean;
} {
	const candidateIndexes = getIndexesForPrimitiveField(field, { kind: [IndexKind.Primary] });

	const info = {
		isPrimaryKey: false,
		isCompositePrimaryKey: false,
	};

	for (const index of candidateIndexes) {
		// candidateIndexes is guaranteed to only contain primary indexes and indexes that contain field
		if (index.fields.length === 1) {
			info.isPrimaryKey = true;
		}
		else {
			info.isCompositePrimaryKey = true;
		}
	}

	return info;
}

/**
 * Retrieves the single unique fields from a given collection.
 * This includes fields that are part of primary or unique indexes and consist of a single field.
 *
 * @param {ResolvedMonospaceCollection} collection - The collection to extract unique fields from.
 * @return {ResolvedMonospacePrimitiveField[]} An array of unique primitive fields found in the collection.
 */
export function getCollectionSingleUniqueFields(collection: ResolvedMonospaceCollection): ResolvedMonospacePrimitiveField[] {
	if (!collection) {
		return [];
	}

	return (collection.indexes?.filter((index) =>
		(index.kind === IndexKind.Primary || index.kind === IndexKind.Unique) && index.fields.length === 1,
	)
		.flatMap((index) => index.fields?.map((indexField) => indexField.field))
		.filter(Boolean) ?? []) as ResolvedMonospacePrimitiveField[];
}

/**
 * Analyzes a primitive field to determine its uniqueness properties.
 *
 * @param {ResolvedMonospacePrimitiveField} field - The primitive field to analyze.
 * @return {object} An object containing the uniqueness information of the field:
 * - `isUnique` (boolean): Indicates if the field is uniquely indexed on its own.
 * - `isCompositeUnique` (boolean): Indicates if the field is part of a composite unique index.
 */
export function getPrimitiveFieldUniqueInfo(field: ResolvedMonospacePrimitiveField): {
	isUnique: boolean;
	isCompositeUnique: boolean;
} {
	const candidateIndexes = getIndexesForPrimitiveField(field, { kind: [IndexKind.Unique, IndexKind.Primary] });

	const info = {
		isUnique: false,
		isCompositeUnique: false,
	};

	for (const index of candidateIndexes) {
		// candidateIndexes is guaranteed to only contain unique indexes and indexes that contain field
		if (index.fields.length === 1) {
			info.isUnique = true;
		}
		else {
			info.isCompositeUnique = true;
		}
	}

	return info;
}

export function isPrimitiveField(field: ResolvedMonospacePrimitiveField | ResolvedMonospaceSingleRelationField): field is ResolvedMonospacePrimitiveField;
export function isPrimitiveField(field: MonospacePrimitiveField | MonospaceSingleRelationField): field is MonospacePrimitiveField;

export function isPrimitiveField(field: MonospacePrimitiveField | MonospaceSingleRelationField): boolean {
	return 'type' in field && 'dbName' in field;
}

export function isSingleRelationField(field: ResolvedMonospacePrimitiveField | ResolvedMonospaceSingleRelationField): field is ResolvedMonospaceSingleRelationField;
export function isSingleRelationField(field: MonospacePrimitiveField | MonospaceSingleRelationField): field is MonospaceSingleRelationField;

export function isSingleRelationField(field: MonospacePrimitiveField | MonospaceSingleRelationField): boolean {
	return 'apiName' in field && !isPrimitiveField(field);
}

export function newCollectionId() {
	return randomUuid() as GlobalCollectionId;
}

export function newPrimitiveFieldId() {
	return randomUuid() as GlobalPrimitiveFieldId;
}

export function newSingleRelationFieldId() {
	return randomUuid() as GlobalSingleRelationFieldId;
}
